import React, { useState, useEffect } from 'react';

const normalizeTrackingId = (value) => {
  if (typeof value === 'string') return value.trim();
  if (value === undefined || value === null) return '';
  return String(value).trim();
};
export default function FirstTimeSetup({ mode = 'create' }) {
  const [form, setForm] = useState({
    adminEmail: '',
    adminPassword: '',
    brandName: '',
    brandAddress: '',
    contactEmail: '',
    domain: '',
    subdomainFrontend: '',
    subdomainAdmin: '',
    subdomainBackend: '',
    mongodbUri: '',
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPass: '',
    smtpFrom: '',
    backendApiBase: '',
    // Theme fields
    bgColor: '',
    formBgColor: '',
    buttonBgColor: '',
    buttonTextColor: '',
    clarityEnabled: false,
    clarityTrackingId: '',
    gaEnabled: false,
    gaMeasurementId: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [backendBase, setBackendBase] = useState('');
  const [restarting, setRestarting] = useState(false);
  const [initial, setInitial] = useState(null); // snapshot for diffing in edit mode

  // Resolve backend base from current admin host if not provided
  const computeBackendBaseFromWindow = () => {
    try {
      const { protocol, hostname } = window.location;
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        const domain = parts.slice(1).join('.');
        return `${protocol}//backend.${domain}`;
      }
    } catch {}
    return '';
  };

  // Prefill form in edit mode from platform_data
  useEffect(() => {
    if (mode !== 'edit') return;
    const base = computeBackendBaseFromWindow();
    if (!base) return;
    setBackendBase(base);
    (async () => {
      try {
        const res = await fetch(`${base}/api/platform-data`);
        const js = await res.json().catch(() => ({}));
        if (res.ok && js && js.data) {
          const d = js.data;
          const loaded = {
            ...form,
            adminEmail: d.adminEmail ?? form.adminEmail,
            brandName: d.brandName ?? form.brandName ?? '',
            brandAddress: d.brandAddress ?? form.brandAddress ?? '',
            contactEmail: d.contactEmail ?? form.contactEmail ?? '',
            domain: d.domain ?? form.domain ?? '',
            subdomainFrontend: d.subdomainFrontend ?? form.subdomainFrontend ?? '',
            subdomainAdmin: d.subdomainAdmin ?? form.subdomainAdmin ?? '',
            subdomainBackend: d.subdomainBackend ?? form.subdomainBackend ?? '',
            mongodbUri: d.mongodbUri ?? form.mongodbUri ?? '',
            smtpHost: d.smtp?.host ?? form.smtpHost ?? '',
            smtpPort: d.smtp?.port?.toString?.() ?? form.smtpPort ?? '',
            smtpUser: d.smtp?.user ?? form.smtpUser ?? '',
            smtpPass: d.smtp?.pass ?? form.smtpPass ?? '',
            smtpFrom: d.smtp?.from ?? form.smtpFrom ?? '',
            // Theme
            bgColor: d.theme?.backgroundColor ?? form.bgColor ?? '',
            formBgColor: d.theme?.formBackgroundColor ?? form.formBgColor ?? '',
            buttonBgColor: d.theme?.buttonBgColor ?? form.buttonBgColor ?? '',
            buttonTextColor: d.theme?.buttonTextColor ?? form.buttonTextColor ?? '',
            clarityEnabled: Boolean(d.tracking?.clarity?.enabled),
            clarityTrackingId: normalizeTrackingId(d.tracking?.clarity?.trackingId ?? form.clarityTrackingId ?? ''),
            gaEnabled: Boolean(d.tracking?.googleAnalytics?.enabled),
            gaMeasurementId: normalizeTrackingId(d.tracking?.googleAnalytics?.measurementId ?? form.gaMeasurementId ?? ''),
          };
          setForm(loaded);
          setInitial({
            adminEmail: loaded.adminEmail,
            brandName: loaded.brandName,
            brandAddress: loaded.brandAddress,
            contactEmail: loaded.contactEmail,
            domain: loaded.domain,
            subdomainFrontend: loaded.subdomainFrontend,
            subdomainAdmin: loaded.subdomainAdmin,
            subdomainBackend: loaded.subdomainBackend,
            mongodbUri: loaded.mongodbUri,
            smtpHost: loaded.smtpHost,
            smtpPort: loaded.smtpPort,
            smtpUser: loaded.smtpUser,
            smtpPass: loaded.smtpPass,
            smtpFrom: loaded.smtpFrom,
            bgColor: loaded.bgColor,
            formBgColor: loaded.formBgColor,
            buttonBgColor: loaded.buttonBgColor,
            buttonTextColor: loaded.buttonTextColor,
            clarityEnabled: loaded.clarityEnabled,
            clarityTrackingId: loaded.clarityTrackingId,
            gaEnabled: loaded.gaEnabled,
            gaMeasurementId: loaded.gaMeasurementId,
          });
        }
      } catch {}
    })();
  }, [mode]);
  const update = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    setLogoFile(file || null);
  };
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      // Warn if password change in edit mode
      if (mode === 'edit' && (form.adminPassword || '').trim()) {
        const ok = (typeof window !== 'undefined') && window.confirm('You are changing the admin password. You will be logged out and must log in again with the new password. Continue?');
        if (!ok) { setSubmitting(false); return; }
      }
      let logoBase64 = '';
      if (logoFile) {
        logoBase64 = await toBase64(logoFile);
      }
      if (!form.backendApiBase && mode === 'create') {
        throw new Error('Please provide Backend API Base URL');
      }
      let payload;
      if (mode === 'edit') {
        // Build a minimal patch with only changed fields
        const patch = {};
        const changed = (k) => initial && form[k] !== initial[k] && form[k] !== undefined;
        if (changed('adminEmail')) patch.adminEmail = form.adminEmail;
        if ((form.adminPassword || '').trim()) patch.adminPassword = form.adminPassword;
        if (changed('brandName')) patch.brandName = form.brandName;
        if (changed('brandAddress')) patch.brandAddress = form.brandAddress;
        if (changed('contactEmail')) patch.contactEmail = form.contactEmail;
        if (changed('domain')) patch.domain = form.domain;
        if (changed('subdomainFrontend')) patch.subdomainFrontend = form.subdomainFrontend;
        if (changed('subdomainAdmin')) patch.subdomainAdmin = form.subdomainAdmin;
        if (changed('subdomainBackend')) patch.subdomainBackend = form.subdomainBackend;
        if (changed('mongodbUri')) patch.mongodbUri = form.mongodbUri;
        // SMTP diff
        const smtp = {};
        if (changed('smtpHost')) smtp.host = form.smtpHost;
        if (changed('smtpPort')) smtp.port = Number(form.smtpPort) || 0;
        if (changed('smtpUser')) smtp.user = form.smtpUser;
        if (changed('smtpPass')) smtp.pass = form.smtpPass;
        if (changed('smtpFrom')) smtp.from = form.smtpFrom;
        if (Object.keys(smtp).length) patch.smtp = smtp;
        // Theme diff
        const theme = {};
        if (changed('bgColor')) theme.backgroundColor = form.bgColor;
        if (changed('formBgColor')) theme.formBackgroundColor = form.formBgColor;
        if (changed('buttonBgColor')) theme.buttonBgColor = form.buttonBgColor;
        if (changed('buttonTextColor')) theme.buttonTextColor = form.buttonTextColor;
        if (Object.keys(theme).length) patch.theme = theme;
        // Tracking diff
        const tracking = {};
        const clarityChanged = changed('clarityEnabled') || changed('clarityTrackingId');
        if (clarityChanged) {
          tracking.clarity = {};
          if (changed('clarityEnabled')) tracking.clarity.enabled = Boolean(form.clarityEnabled);
          if (changed('clarityTrackingId')) tracking.clarity.trackingId = normalizeTrackingId(form.clarityTrackingId);
        }
        const gaChanged = changed('gaEnabled') || changed('gaMeasurementId');
        if (gaChanged) {
          tracking.googleAnalytics = {};
          if (changed('gaEnabled')) tracking.googleAnalytics.enabled = Boolean(form.gaEnabled);
          if (changed('gaMeasurementId')) tracking.googleAnalytics.measurementId = normalizeTrackingId(form.gaMeasurementId);
        }
        if (Object.keys(tracking).length) patch.tracking = tracking;
        // Logo only if provided
        if (logoBase64) patch.logoBase64 = logoBase64;
        payload = patch;
      } else {
        // Full payload for create
        payload = {
          adminEmail: form.adminEmail,
          adminPassword: form.adminPassword,
          brandName: form.brandName,
          brandAddress: form.brandAddress,
          contactEmail: form.contactEmail,
          domain: form.domain,
          subdomainFrontend: form.subdomainFrontend,
          subdomainAdmin: form.subdomainAdmin,
          subdomainBackend: form.subdomainBackend,
          mongodbUri: form.mongodbUri,
          smtp: {
            host: form.smtpHost,
            port: Number(form.smtpPort) || 0,
            user: form.smtpUser,
            pass: form.smtpPass,
            from: form.smtpFrom,
          },
          logoBase64,
          theme: {
            backgroundColor: form.bgColor,
            formBackgroundColor: form.formBgColor,
            buttonBgColor: form.buttonBgColor,
            buttonTextColor: form.buttonTextColor,
          },
        };
      }
      // Resolve backend base
      let base = '';
      if (mode === 'create') {
        base = (form.backendApiBase || '').trim();
      } else {
        base = (backendBase || computeBackendBaseFromWindow() || '').trim();
      }
      // Normalize/upgrade backend base URL to avoid mixed-content when page is HTTPS
      if (base && !/^https?:\/\//i.test(base)) {
        base = `${window.location.protocol}//${base.replace(/^\/*/, '')}`;
      }
      if (!base) {
        throw new Error('Unable to resolve Backend API base URL');
      }
      if (window.location.protocol === 'https:' && base.startsWith('http://')) {
        base = 'https://' + base.slice(7);
      }
      const endpoint = mode === 'create'
        ? `${base.replace(/\/$/, '')}/api/setup/first-time`
        : `${base.replace(/\/$/, '')}/api/setup/update`;
      setBackendBase(base.replace(/\/$/, ''));

      const res = await fetch(endpoint, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Setup failed');
      }
      setMessage(mode === 'create' ? 'Setup completed successfully. You can now log in to admin.' : 'Configuration updated successfully.');

      // If backend provided the final Mongo URI, display it for convenience
      if (data.mongodbUri) {
        setStatus((prev) => ({ ...(prev || {}), mongodbUri: data.mongodbUri }));
      }

      // Write frontend envs, then fetch status for visual confirmation
      try {
        await fetch(`${base.replace(/\/$/, '')}/api/setup/sync-frontend-envs`, { method: 'POST' });
        const statusRes = await fetch(`${base.replace(/\/$/, '')}/api/setup/status`);
        const statusJson = await statusRes.json().catch(() => ({}));
        if (statusRes.ok && statusJson && statusJson.data) {
          setStatus(statusJson.data);
        }
        if (mode === 'edit') {
          // Reload to ensure UI picks latest env-based branding in dev
          if ((form.adminPassword || '').trim()) {
            // Force logout if password changed
            try { localStorage.removeItem('authToken'); } catch {}
          }
          setTimeout(() => { if (typeof window !== 'undefined') window.location.reload(); }, 800);
        }
      } catch (_) {}
    } catch (err) {
      setMessage(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestart = async () => {
    if (!backendBase) return;
    setRestarting(true);
    try {
      const resp = await fetch(`${backendBase}/api/setup/startup/run`, { method: 'POST' });
      const js = await resp.json().catch(() => ({}));
      if (!resp.ok || js.success === false) {
        throw new Error(js.message || 'Failed to trigger restart');
      }
      alert('Startup script triggered. Services may restart shortly.');
    } catch (e) {
      alert(e.message || 'Failed to trigger restart');
    } finally {
      setRestarting(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-3xl mx-auto bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-semibold mb-6">First Time Setup</h1>
        {message && (
          <div className="mb-4 p-3 rounded text-sm"
               style={{ background: message.includes('success') ? '#dcfce7' : '#fee2e2', color: '#111827' }}>
            {message}
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Admin Email</label>
              <input name="adminEmail" value={form.adminEmail} onChange={update} className="w-full border p-2 rounded" required={mode==='create'} />
            </div>
            <div>
              <label className="block text-sm font-medium">Admin Password</label>
              <input name="adminPassword" type="password" value={form.adminPassword} onChange={update} className="w-full border p-2 rounded" required={mode==='create'} />
            </div>
            {mode === 'create' ? (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Backend API Base URL</label>
                <input name="backendApiBase" placeholder="https://backend.example.com" value={form.backendApiBase} onChange={update} className="w-full border p-2 rounded" required />
              </div>
            ) : null}
          </div>
          <hr className="my-2" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Brand Name</label>
              <input name="brandName" value={form.brandName} onChange={update} className="w-full border p-2 rounded" required={mode==='create'} />
            </div>
            <div>
              <label className="block text-sm font-medium">Brand Address</label>
              <input name="brandAddress" value={form.brandAddress} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">Contact Email</label>
              <input name="contactEmail" value={form.contactEmail} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">Domain</label>
              <input name="domain" placeholder="abcchat.ai" value={form.domain} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">Subdomain (Frontend)</label>
              <input name="subdomainFrontend" placeholder="app" value={form.subdomainFrontend} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">Subdomain (Admin)</label>
              <input name="subdomainAdmin" placeholder="admin" value={form.subdomainAdmin} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">Subdomain (Backend)</label>
              <input name="subdomainBackend" placeholder="api" value={form.subdomainBackend} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium">MongoDB Connection String</label>
              <input name="mongodbUri" placeholder="mongodb://user:pass@10.0.0.5:27017" value={form.mongodbUri} onChange={update} className="w-full border p-2 rounded" required={mode==='create'} />
            </div>
          </div>
          <hr className="my-2" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">SMTP Host</label>
              <input name="smtpHost" value={form.smtpHost} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">SMTP Port</label>
              <input name="smtpPort" value={form.smtpPort} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">SMTP User</label>
              <input name="smtpUser" value={form.smtpUser} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium">SMTP Password</label>
              <input name="smtpPass" type="password" value={form.smtpPass} onChange={update} className="w-full border p-2 rounded" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium">SMTP From Email</label>
              <input name="smtpFrom" value={form.smtpFrom} onChange={update} className="w-full border p-2 rounded" />
            </div>
          </div>

          <hr className="my-2" />

          {mode === 'edit' && (
            <>
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Analytics & Tracking</h2>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium text-gray-900">Microsoft Clarity</p>
                      <p className="text-sm text-gray-500">Session replay and heatmaps across deployments.</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{form.clarityEnabled ? 'Enabled' : 'Disabled'}</span>
                      <input
                        type="checkbox"
                        name="clarityEnabled"
                        checked={!!form.clarityEnabled}
                        onChange={update}
                        className="h-5 w-5"
                      />
                    </label>
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium">Clarity Tracking ID</label>
                    <input
                      name="clarityTrackingId"
                      value={form.clarityTrackingId}
                      onChange={update}
                      placeholder="e.g. tjrdg2334t"
                      className="w-full border p-2 rounded"
                      disabled={!form.clarityEnabled}
                    />
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium text-gray-900">Google Analytics 4</p>
                      <p className="text-sm text-gray-500">Use a measurement ID that starts with G-.</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{form.gaEnabled ? 'Enabled' : 'Disabled'}</span>
                      <input
                        type="checkbox"
                        name="gaEnabled"
                        checked={!!form.gaEnabled}
                        onChange={update}
                        className="h-5 w-5"
                      />
                    </label>
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium">GA4 Measurement ID</label>
                    <input
                      name="gaMeasurementId"
                      value={form.gaMeasurementId}
                      onChange={update}
                      placeholder="G-XXXXXXXXXX"
                      className="w-full border p-2 rounded"
                      disabled={!form.gaEnabled}
                    />
                  </div>
                </div>
              </div>

              <hr className="my-2" />
            </>
          )}

          {/* Theme color placeholders (stored but not applied to Admin UI now) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Background Color</label>
              <input
                name="bgColor"
                placeholder="#f8fafc or rgb(248,250,252)"
                value={form.bgColor}
                onChange={update}
                className="w-full border p-2 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Form Background Color</label>
              <input
                name="formBgColor"
                placeholder="#ffffff"
                value={form.formBgColor}
                onChange={update}
                className="w-full border p-2 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Button Background Color</label>
              <input
                name="buttonBgColor"
                placeholder="#0f172a"
                value={form.buttonBgColor}
                onChange={update}
                className="w-full border p-2 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Button Text Color</label>
              <input
                name="buttonTextColor"
                placeholder="#ffffff"
                value={form.buttonTextColor}
                onChange={update}
                className="w-full border p-2 rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium">Logo</label>
            <input type="file" accept="image/*" onChange={handleLogo} className="w-full" />
          </div>
          <div className="pt-2 flex gap-3">
            <button disabled={submitting} className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50">
              {submitting ? (mode === 'create' ? 'Setting up…' : 'Updating…') : (mode === 'create' ? 'Run Setup' : 'Update Config')}
            </button>
            {mode === 'create' ? null : (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const base = (backendBase || window.location.origin.replace(/^(https?:\/\/)[^.]+\./, '$1backend.'));
                    // 1) Sync env files from platform_data
                    await fetch(`${base}/api/setup/sync-frontend-envs`, { method: 'POST' });
                    // 2) Reload existing PM2 processes only (no new nodes)
                    await fetch(`${base}/api/setup/startup/run`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ reloadOnly: true })
                    });
                    alert('Branding synced and PM2 reload triggered. Refresh in a few seconds.');
                  } catch (e) {
                    alert('Sync branding failed');
                  }
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
              >
                Sync Branding
              </button>
            )}
          </div>
        </form>

        {status?.collections && (
          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-3">Setup Status</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2 border">Collection</th>
                    <th className="text-left p-2 border">Exists</th>
                    <th className="text-left p-2 border">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {status.collections.map((c) => (
                    <tr key={c.collection} className="border-b">
                      <td className="p-2 border">{c.collection}</td>
                      <td className="p-2 border">{c.exists ? 'Yes' : 'No'}</td>
                      <td className="p-2 border">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {status?.mongodbUri && (
          <div className="mt-4 p-3 bg-gray-50 border rounded">
            <div className="text-sm text-gray-700">
              <div className="font-semibold mb-1">Recommended MONGODB_URI for Backend/.env</div>
              <code className="break-all">{status.mongodbUri}</code>
            </div>
          </div>
        )}

        {mode === 'create' && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleRestart}
              disabled={!backendBase || restarting}
              className="px-4 py-2 bg-slate-700 text-white rounded disabled:opacity-50"
            >
              {restarting ? 'Triggering restart…' : 'Run Startup Script (Restart)'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
